import React from 'react';
import { dpsTheme } from '../../../styles/dpsTheme';
import {
  formatBurglaryCount,
  formatBurglaryRate,
  formatBurglaryMessage,
  getBurglaryRateLabel,
  isBelowStatisticalThreshold
} from '../../../utils/formatters';

interface CrimeData {
  annee: string | number;
  commune?: string;
  codeInsee?: string;
  cambriolages: number;
  tentativesCambriolages?: number;
  statutDiffusion?: string;
  tauxPour1000Logements: number;
}

interface CrimeComparison {
  decile: number;
  communeRate: number;
  departmentRate: number;
  nationalRate: number;
  isAboveAverage?: boolean;
  percentileRank?: number;
  relativeDifference?: number;
}

interface RiskAnalysisCardProps {
  crimeData?: CrimeData;
  crimeComparison?: CrimeComparison;
  loading?: boolean;
}

export const RiskAnalysisCard: React.FC<RiskAnalysisCardProps> = ({
  crimeData,
  crimeComparison,
  loading = false,
}) => {
  // Chargement en cours
  if (loading) {
    return (
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: `1px solid ${dpsTheme.colors.border.primary}`,
          borderRadius: dpsTheme.borderRadius.md,
          padding: dpsTheme.spacing.xl,
          display: 'flex',
          alignItems: 'center',
          gap: dpsTheme.spacing.md,
        }}
      >
        <div style={{
          width: '20px',
          height: '20px',
          border: '2px solid rgba(255,255,255,0.1)',
          borderTop: '2px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          flexShrink: 0,
        }} />
        <span style={{ fontSize: dpsTheme.typography.fontSize.base, color: dpsTheme.colors.text.muted }}>
          Chargement des statistiques de criminalité…
        </span>
      </div>
    );
  }

  // Si pas de données disponibles (secret statistique ou commune non trouvée)
  if (!crimeData || !crimeComparison) {
    return (
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: `1px solid ${dpsTheme.colors.border.primary}`,
          borderRadius: dpsTheme.borderRadius.md,
          padding: dpsTheme.spacing.xl,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: dpsTheme.spacing.md,
            marginBottom: dpsTheme.spacing.md,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div style={{ fontSize: dpsTheme.typography.fontSize.lg, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.muted }}>
            Données non disponibles (secret statistique)
          </div>
        </div>
        <p style={{ fontSize: dpsTheme.typography.fontSize.base, color: dpsTheme.colors.text.tertiary, lineHeight: dpsTheme.typography.lineHeight.relaxed }}>
          Les données de criminalité pour votre commune ne sont pas publiées publiquement. Cela indique généralement un nombre de cambriolages trop faible pour être diffusé individuellement, ce qui est plutôt une bonne nouvelle pour votre secteur.
        </p>
      </div>
    );
  }

  const rate = crimeData.tauxPour1000Logements;
  const communeRate = crimeComparison.communeRate || rate;
  const departmentRate = crimeComparison.departmentRate || 5.0; // Fallback si pas de données
  const nationalRate = crimeComparison.nationalRate || 3.95; // Moyenne nationale réelle


  // LOGIQUE SIMPLE BASÉE SUR LE TAUX RÉEL (pas le décile !)
  let riskLevel: 'low' | 'medium' | 'high';
  let riskLabel: string;
  let riskColor: string;
  let riskIcon: JSX.Element;
  let riskMessage: string;

  if (rate < 2.5) {
    // Bien en dessous de la moyenne nationale
    riskLevel = 'low';
    riskLabel = 'Excellente sécurité';
    riskColor = '#22c55e';
    riskIcon = (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={riskColor} strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    );
    riskMessage = formatBurglaryMessage(crimeData.cambriolages, rate, crimeData.annee, nationalRate, 'excellent');
  } else if (rate < 3.5) {
    // En dessous de la moyenne nationale
    riskLevel = 'low';
    riskLabel = 'Très bonne sécurité';
    riskColor = '#10b981';
    riskIcon = (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={riskColor} strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    );
    riskMessage = formatBurglaryMessage(crimeData.cambriolages, rate, crimeData.annee, nationalRate, 'good');
  } else if (rate >= 3.5 && rate <= 4.5) {
    // Dans la moyenne nationale
    riskLevel = 'medium';
    riskLabel = 'Sécurité moyenne';
    riskColor = '#f59e0b';
    riskIcon = (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={riskColor} strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
    riskMessage = formatBurglaryMessage(crimeData.cambriolages, rate, crimeData.annee, nationalRate, 'average');
  } else if (rate > 4.5 && rate <= 6) {
    // Au-dessus de la moyenne nationale
    riskLevel = 'medium';
    riskLabel = 'Vigilance recommandée';
    riskColor = '#f97316';
    riskIcon = (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={riskColor} strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
    riskMessage = formatBurglaryMessage(crimeData.cambriolages, rate, crimeData.annee, nationalRate, 'warning');
  } else {
    // Nettement au-dessus de la moyenne
    riskLevel = 'high';
    riskLabel = 'Risque élevé';
    riskColor = '#ef4444';
    riskIcon = (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={riskColor} strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
    riskMessage = formatBurglaryMessage(crimeData.cambriolages, rate, crimeData.annee, nationalRate, 'high');
  }

  // Recommandations selon le niveau de risque
  const recommendations = riskLevel === 'high' ? [
    'Installer un système d\'alarme avec télésurveillance professionnelle',
    'Équiper votre logement de caméras de surveillance visibles',
    'Renforcer tous les points d\'accès avec des protections physiques (porte blindée, serrures multipoint)',
  ] : riskLevel === 'medium' ? [
    'Installer un système d\'alarme connecté',
    'Ajouter des détecteurs de mouvement et d\'ouverture',
    'Mettre en place un simulateur de présence lors de vos absences',
  ] : [
    'Maintenir les mesures de prévention habituelles',
    'Veiller à toujours verrouiller portes et fenêtres',
    'Signaler tout comportement suspect aux autorités',
  ];

  const maxRate = Math.max(communeRate, departmentRate, nationalRate);

  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: `1px solid ${riskColor}40`,
        borderRadius: dpsTheme.borderRadius.md,
        padding: dpsTheme.spacing.xl,
      }}
    >
      {/* En-tête avec niveau de risque */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: dpsTheme.spacing.md,
          marginBottom: dpsTheme.spacing.lg,
          padding: dpsTheme.spacing.md,
          background: `${riskColor}15`,
          borderRadius: dpsTheme.borderRadius.sm,
          border: `1px solid ${riskColor}30`,
        }}
      >
        {riskIcon}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: dpsTheme.typography.fontSize.xl, fontWeight: dpsTheme.typography.fontWeight.bold, color: riskColor }}>
            Niveau de risque : {riskLabel}
          </div>
          <p style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.tertiary, marginTop: '4px', marginBottom: 0 }}>
            {riskMessage}
          </p>
        </div>
      </div>

      {/* Statistiques principales */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: dpsTheme.spacing.lg, marginBottom: dpsTheme.spacing.xl }}>
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: `1px solid ${dpsTheme.colors.border.primary}`,
            borderRadius: dpsTheme.borderRadius.sm,
            padding: dpsTheme.spacing.lg,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: dpsTheme.typography.fontSize['4xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary }}>
            {formatBurglaryCount(crimeData.cambriolages)}
          </div>
          <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted, marginTop: '4px' }}>
            Cambriolages en {crimeData.annee}
          </div>
        </div>
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: `1px solid ${dpsTheme.colors.border.primary}`,
            borderRadius: dpsTheme.borderRadius.sm,
            padding: dpsTheme.spacing.lg,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: dpsTheme.typography.fontSize['4xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary }}>
            {formatBurglaryRate(rate)}
            {rate > 0 && <span style={{ fontSize: dpsTheme.typography.fontSize.xl }}>‰</span>}
            {(rate === 0 || rate < 0.005) && <span style={{ fontSize: dpsTheme.typography.fontSize.xl }}>‰</span>}
          </div>
          <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted, marginTop: '4px' }}>
            {getBurglaryRateLabel(rate)}
          </div>
        </div>
      </div>

      {/* Comparaison territoriale */}
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: dpsTheme.borderRadius.sm,
          padding: dpsTheme.spacing.lg,
          marginBottom: dpsTheme.spacing.xl,
        }}
      >
        <div style={{ fontSize: dpsTheme.typography.fontSize.base, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary, marginBottom: dpsTheme.spacing.md }}>
          Comparaison territoriale
        </div>
        
        {/* Votre commune */}
        <div style={{ marginBottom: dpsTheme.spacing.md }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.tertiary }}>
              Votre commune{crimeData.commune ? ` (${crimeData.commune})` : ''}
            </span>
            <span style={{ fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold, color: riskColor }}>
              {formatBurglaryRate(communeRate)}{(communeRate === 0 || communeRate < 0.005) ? '‰' : '‰'}
            </span>
          </div>
          <div style={{ width: '100%', height: '8px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${(communeRate / maxRate) * 100}%`, height: '100%', background: riskColor, borderRadius: '4px', transition: 'width 0.5s ease' }} />
          </div>
        </div>

        {/* Moyenne départementale */}
        <div style={{ marginBottom: dpsTheme.spacing.md }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.tertiary }}>Moyenne départementale</span>
            <span style={{ fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold, color: '#3b82f6' }}>
              {departmentRate.toFixed(2)}‰
            </span>
          </div>
          <div style={{ width: '100%', height: '8px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${(departmentRate / maxRate) * 100}%`, height: '100%', background: '#3b82f6', borderRadius: '4px', transition: 'width 0.5s ease' }} />
          </div>
        </div>

        {/* Moyenne nationale */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.tertiary }}>Moyenne nationale (France)</span>
            <span style={{ fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.muted }}>
              {nationalRate.toFixed(2)}‰
            </span>
          </div>
          <div style={{ width: '100%', height: '8px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${(nationalRate / maxRate) * 100}%`, height: '100%', background: dpsTheme.colors.text.disabled, borderRadius: '4px', transition: 'width 0.5s ease' }} />
          </div>
        </div>
      </div>

      {/* Recommandations contextuelles */}
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: dpsTheme.borderRadius.sm,
          padding: dpsTheme.spacing.lg,
        }}
      >
        <div style={{ fontSize: dpsTheme.typography.fontSize.base, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary, marginBottom: dpsTheme.spacing.md }}>
          Recommandations adaptées au niveau de risque local
        </div>
        {recommendations.map((rec, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: '8px 0',
              fontSize: dpsTheme.typography.fontSize.sm,
              color: dpsTheme.colors.text.tertiary,
            }}
          >
            <span style={{ color: riskColor, fontWeight: dpsTheme.typography.fontWeight.bold, minWidth: '20px' }}>
              {index + 1}.
            </span>
            {rec}
          </div>
        ))}
      </div>

      {/* Source des données */}
      <div
        style={{
          marginTop: dpsTheme.spacing.lg,
          padding: dpsTheme.spacing.md,
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: dpsTheme.borderRadius.sm,
          fontSize: dpsTheme.typography.fontSize.xs,
          color: dpsTheme.colors.text.disabled,
          textAlign: 'center',
        }}
      >
        Source : Ministère de l'Intérieur via data.gouv.fr • Données {crimeData.annee}
        <br />
        Le taux pour 1000 logements permet de comparer les communes indépendamment de leur taille
      </div>
    </div>
  );
};